import { html, useEffect, useState } from '/vendor/htm-preact.module.js';
import { get } from '/api.js';
import { useQrSvg } from '/qr.js';

/**
 * The QR beside the table.
 *
 * Shown on a match being scored, so somebody can point a camera at it and
 * follow the frame without an account. The token is minted on demand rather
 * than at match creation: it is only useful while the match is in progress,
 * and asking for it when the screen opens keeps it out of anything stored.
 */
export function WatchQr({ matchId }) {
  const [path, setPath] = useState(null);
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    let timer = null;
    const mint = () => {
      get(`/api/matches/${encodeURIComponent(matchId)}/watch`)
        .then((r) => {
          if (!live) return;
          setPath(r.path);
          // The token expires now, so a screen left showing one must ask for
          // another before it lapses. Half its life, so what is on the wall
          // is never close to dying.
          const life = (r.expires_in || 3600) * 1000;
          timer = setTimeout(mint, Math.max(60000, life / 2));
        })
        // Say so. Setting path back to null rendered as "Loading…" for ever,
        // which is the same silent dead end this app has now met three times.
        .catch((e) => {
          if (live) setFailed(e.detail || e.message || 'no answer');
        });
    };
    mint();
    return () => { live = false; if (timer) clearTimeout(timer); };
  }, [open, matchId]);

  if (!open) {
    return html`
      <button class="secondary" data-testid="watch-qr-open"
              onClick=${() => setOpen(true)}>Let people watch</button>`;
  }
  if (failed) {
    return html`
      <p class="sub" data-testid="watch-qr-error">
        ${`No watch link for this match — ${failed}.`}
      </p>`;
  }
  if (!path) return html`<p class="sub">Loading…</p>`;

  const url = `${location.origin}${path}`;
  const svg = useQrSvg(url);

  return html`
    <div class="card" data-testid="watch-qr" style="text-align:center">
      <p class="sub" style="margin:0 0 .6rem">
        Point a camera at this to follow the score. No account needed, and it
        stops working when the match ends.
      </p>
      <div class="qr" data-testid="watch-qr-svg"
           dangerouslySetInnerHTML=${{ __html: svg || '' }}></div>
      <button class="secondary" onClick=${() => setOpen(false)}>Hide</button>
    </div>`;
}
